import { useCallback, useEffect, useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_ACCOUNT_EVENTS } from 'pi-kiosk-shared';
import { buildScreenMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import {
  resolveCustomerSignInPath,
  resolveCustomerSignUpPath,
} from '../features/auth/customerAuthPaths.js';
import { isGuestAuthPath } from '../features/auth/routing/isGuestAuthPath.js';
import { useLogout } from '../features/auth/authApi.js';
import { isGloballySignedInChrome } from '../features/hub/customerHubPaths.js';
import { resolvePostLogoutLandingPath } from '../features/shop/resolveShopHref.js';
import { useCustomerAnalytics } from '../shared/analytics/CustomerAnalyticsProvider.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import {
  inferPaymentResumeReturnSource,
  isPaymentResumeReturnPath,
} from '../shared/session/isPaymentResumeReturnPath.js';
import { parseDirectoryViewMode } from '../shared/session/directoryViewMode.js';
import {
  clearReturnTarget,
  isMapResumeReturnPath,
  parkMapResumeReturnTarget,
  parkPaymentResumeReturnTarget,
  savePlatformReturnTarget,
  saveReturnTarget,
  type ReturnTargetSource,
} from '../shared/session/returnTarget.js';
import { useTenantCode } from '../shared/tenant/TenantProvider.js';
import {
  ChevronRightIcon,
  SignOutIcon,
  UserCircleIcon,
} from '../shared/ui/BottomNavIcons.js';
import { Button } from '../shared/ui/Button.js';
import { showToast } from '../shared/ui/toastStore.js';
import { isPlatformChromeTenant } from './platformChromeTenant.js';
import { CustomerChromeTopPanel } from './CustomerChromeTopPanel.js';

export interface CustomerProfileSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const PROFILE_MENU_ROW_CLASS = [
  'flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left',
  'text-[var(--color-on-surface)]',
  'hover:bg-[var(--color-surface-elevated)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
  'disabled:cursor-not-allowed disabled:opacity-60',
].join(' ');

/** Account → payment journey → shop. Account wins over substring `/donate` in donations. */
function inferReturnTargetSource(pathname: string): ReturnTargetSource {
  if (/(?:^|\/)account(?:\/|$)/.test(pathname)) {
    return 'account';
  }
  if (isPaymentResumeReturnPath(pathname)) {
    return inferPaymentResumeReturnSource(pathname) ?? 'shop';
  }
  return 'shop';
}

/**
 * Shell profile panel — account / sign-in / sign-out only.
 * Prefs (language / appearance / guest cookies) live on the Settings gear sheet —
 * not in this panel (USER OVERRIDE; do not fold prefs back here).
 * Guest: Sign in / Register CTAs (icon click never navigates).
 * Signed-in: Account & privacy (signed-in cookies via that route) + Sign out.
 */
export function CustomerProfileSheet({
  open,
  onClose,
}: CustomerProfileSheetProps): JSX.Element {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const location = useLocation();
  const guestDescriptionId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const logout = useLogout();
  const analytics = useCustomerAnalytics();
  const signedIn = isGloballySignedInChrome({ session });

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  /**
   * Parks post-auth return. Payment resume paths succeed only when
   * parkPaymentResumeReturnTarget stores a path (non-null). Map directory
   * (`?view=map`) elevates via parkMapResumeReturnTarget (fail-closed).
   * Bare shops/favorites list and other non-payment paths keep legacy park.
   */
  const parkReturnTarget = useCallback((): boolean => {
    if (isGuestAuthPath(location.pathname)) {
      return true;
    }
    const path = `${location.pathname}${location.search}`;
    // G1 — checkout/donate payment journeys only (not /account/donations).
    if (isPaymentResumeReturnPath(path)) {
      const resumeSource = inferPaymentResumeReturnSource(path);
      if (!isPlatformChromeTenant(tenantCode) && resumeSource !== null) {
        return (
          parkPaymentResumeReturnTarget(path, {
            tenantCode,
            source: resumeSource,
          }) !== null
        );
      }
      return false;
    }
    // G5 — map directory chrome: same elevate as Views CTA; bare list does not.
    if (
      isMapResumeReturnPath(path) &&
      parseDirectoryViewMode(location.search) === 'map'
    ) {
      return (
        parkMapResumeReturnTarget(path, {
          tenantCode,
          source: 'shop',
        }) !== null
      );
    }
    const source = inferReturnTargetSource(location.pathname);
    if (savePlatformReturnTarget(path) === null && !isPlatformChromeTenant(tenantCode)) {
      saveReturnTarget(path, {
        tenantCode,
        source,
      });
    }
    return true;
  }, [location.pathname, location.search, tenantCode]);

  const handleAuthNavigate = useCallback(
    (href: string) => {
      if (!parkReturnTarget()) {
        onClose();
        showToast({
          message: t('shared.unknownError', {
            defaultValue: 'Something went wrong. Please try again.',
          }),
          variant: 'danger',
        });
        return;
      }
      onClose();
      void navigate(href);
    },
    [navigate, onClose, parkReturnTarget, t],
  );

  const handleSignIn = useCallback(() => {
    handleAuthNavigate(resolveCustomerSignInPath(tenantCode));
  }, [handleAuthNavigate, tenantCode]);

  const handleRegister = useCallback(() => {
    handleAuthNavigate(resolveCustomerSignUpPath(tenantCode));
  }, [handleAuthNavigate, tenantCode]);

  const handleAccountAndPrivacy = useCallback(() => {
    onClose();
    const href = isPlatformChromeTenant(tenantCode)
      ? '/account/account-and-privacy'
      : `/${encodeURIComponent(tenantCode)}/account/account-and-privacy`;
    void navigate(href);
  }, [navigate, onClose, tenantCode]);

  const handleSignOut = useCallback(() => {
    const landing = resolvePostLogoutLandingPath(window.location.pathname, tenantCode);
    clearReturnTarget({ tenantCode });
    onClose();
    void (async (): Promise<void> => {
      await analytics.track({
        eventName: ANALYTICS_ACCOUNT_EVENTS.ACCOUNT_LOGGED_OUT,
        screenName: 'account',
        metadata: {
          ...buildScreenMetadata({ screenName: 'account' }),
          logout_scope: 'customer_session',
        },
      });
      try {
        await logout.mutateAsync();
      } finally {
        window.location.replace(landing);
      }
    })();
  }, [analytics, logout, onClose, tenantCode]);

  const title = t('chrome.profile.title', {
    defaultValue: t('journey.myAccount'),
  });
  const signInLabel = t('chrome.profile.signIn', {
    defaultValue: t('journey.signIn'),
  });
  const registerLabel = t('chrome.profile.register', {
    defaultValue: t('app.register.title'),
  });
  const accountLabel = t('account.accountAndPrivacy');
  const accountDescription = t('chrome.profile.accountAndPrivacyDescription', {
    defaultValue: t('account.accountAndPrivacyDescription'),
  });
  const signOutLabel = t('chrome.settings.signOut', {
    defaultValue: t('account.signOut'),
  });
  const signOutDescription = t('chrome.profile.signOutDescription', {
    defaultValue: t('account.settings.signOutDescription'),
  });
  const guestDescription = t('chrome.profile.guestDescription');

  return (
    <CustomerChromeTopPanel
      open={open}
      onClose={onClose}
      title={title}
      titleId="customer-profile-sheet-title"
      describedBy={signedIn ? undefined : guestDescriptionId}
      closeLabel={t('shared.cancel')}
      testId="customer-profile-sheet"
      panelBusy={logout.isPending}
      contentAlign={signedIn ? 'start' : 'center'}
      className="bg-[var(--color-surface)]"
    >
      {signedIn ? (
        <div
          className="flex flex-col"
          role="menu"
          aria-labelledby="customer-profile-sheet-title"
        >
          <button
            type="button"
            role="menuitem"
            className={PROFILE_MENU_ROW_CLASS}
            data-testid="customer-profile-account-and-privacy"
            disabled={logout.isPending}
            onClick={handleAccountAndPrivacy}
          >
            <UserCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-on-surface)]" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold leading-snug">{accountLabel}</span>
              <span className="mt-0.5 block text-xs leading-snug text-[var(--color-on-surface-muted)]">
                {accountDescription}
              </span>
            </span>
            <ChevronRightIcon className="mt-1 h-5 w-5 shrink-0 text-[var(--color-on-surface-muted)]" />
          </button>

          <div
            className="my-1 border-t border-[var(--color-border)]"
            role="separator"
            aria-hidden="true"
          />

          <button
            type="button"
            role="menuitem"
            className={PROFILE_MENU_ROW_CLASS}
            data-testid="account-header-sign-out"
            disabled={logout.isPending}
            aria-busy={logout.isPending || undefined}
            title={signOutLabel}
            onClick={handleSignOut}
          >
            <SignOutIcon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-on-surface)]" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold leading-snug">{signOutLabel}</span>
              <span className="mt-0.5 block text-xs leading-snug text-[var(--color-on-surface-muted)]">
                {signOutDescription}
              </span>
            </span>
          </button>
        </div>
      ) : (
        <div
          className="flex w-full flex-col items-center gap-5 text-center"
          role="group"
          aria-labelledby="customer-profile-sheet-title"
          aria-describedby={guestDescriptionId}
          data-testid="customer-profile-guest-auth"
        >
          <p
            id={guestDescriptionId}
            className="w-full text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-profile-guest-copy"
          >
            {guestDescription}
          </p>
          <div className="flex w-full flex-col items-center gap-3">
            <Button
              type="button"
              intent="primary"
              size="lg"
              block
              onClick={handleSignIn}
              data-testid="customer-profile-sign-in"
            >
              {signInLabel}
            </Button>
            <Button
              type="button"
              intent="secondary"
              size="lg"
              block
              onClick={handleRegister}
              data-testid="customer-profile-register"
            >
              {registerLabel}
            </Button>
          </div>
        </div>
      )}
    </CustomerChromeTopPanel>
  );
}
